import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import * as classTransformer from 'class-transformer';
import {ContainerCollection} from './container-collection';
import {Container} from './container';
import {plainToInstance} from 'class-transformer';
import {CustomField} from '../custom-field/custom-field';
import {Manufacturer} from '../manufacturer/manufacturer';

export interface ContainerCustomFieldValue {
    container: Container;
    customField: CustomField;
    value: string;
}

export interface ContainerUpsertPayload {
    item?: string
    forecast?: string;
    unLoCodeLoadingPort?: string|null;
    unLoCodeDestinationPort?: string|null;
    masterBillOfLadingReference?: string|null;
    oceanCarrierCode?: string|null;
    moduleWattage?: number|null;
    purchaseOrderId?: string|null;
    etdFactory?: Date|null;
    etdPort?: Date|null;
    etaDestinationPort?: Date|null;
    inbondDate?: Date|null;
}

@Injectable({
  providedIn: 'root',
})
export class ContainerService {

    constructor(private httpClient: HttpClient) {
    }

    loadContainers(
        page: number,
        itemsPerPage: number,
        sortField?: string|null,
        sortDirection?: 'asc'|'desc'|null,
        search?: string|null,
        manufacturer?: Manufacturer|null
    ): Observable<ContainerCollection> {
        const params: any = {
            page,
            itemsPerPage
        };

        if (sortField && sortDirection) {
            params['order[' + sortField + ']'] = sortDirection;
        }

        if (search) {
            params['search'] = search;
        }

        if (manufacturer) {
            params['manufacturer'] = manufacturer.id;
        }

        return this.httpClient
            .get('/containers', {params})
            .pipe(map((result: any) => this.hydrateCollection(result)));
    }

    public getAllContainersByForecast(forecastId: string): Observable<ContainerCollection> {
        const params: any = {
            forecast: forecastId,
            itemsPerPage: 1000
        };

        return this.httpClient
            .get('/containers', {params})
            .pipe(map((result: any) => this.hydrateCollection(result)));
    }

    public patch(id: string, payload: Partial<Container>): Observable<Container> {
        return this.httpClient
            .patch(`/containers/${id}`, payload, {
                headers: {'Content-Type': 'application/merge-patch+json'},
            })
            .pipe(map((result: any) => classTransformer.plainToInstance(Container, result)));
    }

    public create(payload: Partial<Container> & { forecast: string }): Observable<Container> {
        return this.httpClient
            .post('/containers', payload, {
                headers: {'Content-Type': 'application/ld+json'},
            })
            .pipe(map((result: any) => classTransformer.plainToInstance(Container, result)));
    }

    private hydrateCollection(result: any): ContainerCollection {
        const collection = new ContainerCollection();
        collection.setTotalItems(result['totalItems']);
        collection.setContainers(
            result['member'].map((containerData: any) => classTransformer.plainToInstance(Container, containerData))
        );

        return collection;
    }

    patchContainers(payload: (ContainerUpsertPayload & { id: string })[]): Observable<Container[]> {
        return this.httpClient.patch<Container[]>('/containers', payload).pipe(
            map(data => data.map(item => plainToInstance(Container, item)))
        );
    }

    createContainer(id: string, payload: ContainerUpsertPayload): Observable<Container> {
        return this.httpClient.put<Container>(`/containers/${id}`, payload).pipe(
            map(data => plainToInstance(Container, data))
        );
    }
}
